<template>
  <div class="pr-2">
    <p class="xPadding titleModal mt-8">
      {{ $t(`billing.labels.info.step0.content`) }}
    </p>
    <!-- analysis Group -->
    <v-row class="xPadding">
      <template>
        <v-simple-table class="erpCode">
          <thead class="erpCode">
            <tr>
              <th>{{ $t(`erpCode`) }}</th>
              <th
                v-for="poNumber in allPONumbers"
                :key="poNumber"
              >
                PO : {{ poNumber }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(counts, erpCode) in organizedTableData"
              :key="erpCode"
            >
              <td>{{ erpCode }}</td>
              <td
                v-for="poNumber in allPONumbers"
                :key="`${erpCode}-${poNumber}`"
              >
                {{ counts[poNumber] ? 'x ' + counts[poNumber] : '' }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </template>
    </v-row>
    <p class="xPadding mt-2 titleModal">
      {{ $t(`billing.labels.info.step0.detail`) }}
    </p>
    <v-row class="xPadding">
      <v-col>
        <VTextField
          :value="`${billingInfo.client.name} [${billingInfo.client.accountNumber}]`"
          outlined
          hideDetails
          :label="$t('forms.labels.client')"
          disabled
        />
      </v-col>
    </v-row>
    <v-row class="xPadding">
      <v-col>
        <VTextField
          :value="`${billingInfo.site.name} [${billingInfo.site.deliveryAccountNumber}]`"
          outlined
          readonly
          hideDetails
          :label="$t('forms.labels.site')"
          disabled
        />
      </v-col>
    </v-row>
    <v-row class="xPadding">
      <v-col>
        <VTextField
          v-model="billingInfo.woNumber"
          outlined
          hideDetails
          :label="$t('labWorkOrders.labels.woNumber')"
          disabled
        />
      </v-col>
    </v-row>
    <v-row class="xPadding">
      <v-col cols="6">
        <VTextField
          v-model="mutableValues.soNumber"
          outlined
          hideDetails
          :label="$t('forms.labels.soNumber')"
          :disabled="isCompleted"
          @input="updateData"
        />
      </v-col>
    </v-row>
    <v-row class="xPadding">
      <v-col>
        <VSelect
          v-model="billingInfo.status"
          outlined
          hideDetails
          itemValue="key"
          itemText="name"
          :label="$t('forms.labels.status')"
          :items="getStatus"
          disabled
        />
      </v-col>
    </v-row>
    <v-row class="xPadding">
      <v-col cols="4">
        <VCheckbox
          v-model="billingInfo.billable"
          :label="$t('forms.labels.billable')"
          disabled
        />
      </v-col>
    </v-row>
  </div>
</template>
<script>
import workOrderStatusChoices from '@/constants/workOrderStatusChoices';
import labBillingStatus from '@/constants/labBillingStatus';
import VCheckbox from '@/components/shared/widgets/forms/VuetifyInputs/VCheckbox.vue';
import { mapGetters } from 'vuex';
import { groupBy } from 'lodash';

export default {
    components: { VCheckbox },
    props: {
        billingInfo: {
            type: Object,
            default: () => [],
        },
    },
    data() {
        return {
            data: {},
            mutableValues: {},
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'teamMainLang',
        ]),
        getStatus() {
            return [workOrderStatusChoices.COMPLETED, workOrderStatusChoices.IN_PROGRESS, workOrderStatusChoices.TODO, labBillingStatus.SENT,
                labBillingStatus.APPROVED, labBillingStatus.SUBMITTED, labBillingStatus.TODO, labBillingStatus.IN_PROGRESS].map(
                status => ({
                    key: status,
                    name: this.$t(`forms.labels.${status}`),
                }),
            );
        },
        isCompleted() {
            return this.billingInfo.status === workOrderStatusChoices.COMPLETED || this.billingInfo.status === labBillingStatus.SENT
            || this.billingInfo.status === labBillingStatus.APPROVED || this.billingInfo.status === labBillingStatus.SUBMITTED;
        },
        organizedTableData() {
            const data = {};

            // Process data from analysis groups
            this.getAnalysisGroups().forEach(item => {
                if (!data[item.ERPCode]) {
                    data[item.ERPCode] = {};
                }
                const poNumber = item.poNumber || this.$t('nonApplicable');
                data[item.ERPCode][poNumber] = (data[item.ERPCode][poNumber] || 0) + item.count;
            });

            // Process data from simple analysis types
            this.getAnalysisSimple().forEach(item => {
                if (!data[item.ERPCode]) {
                    data[item.ERPCode] = {};
                }
                const poNumber = item.poNumber || this.$t('nonApplicable');
                data[item.ERPCode][poNumber] = (data[item.ERPCode][poNumber] || 0) + item.count;
            });

            return data;
        },
        allPONumbers() {
            const poNumbers = new Set();
            [...this.getAnalysisGroups(), ...this.getAnalysisSimple()].forEach(item => {
                poNumbers.add(item.poNumber || this.$t('nonApplicable'));
            });
            return Array.from(poNumbers);
        },
    },
    created() {
        this.mutableValues = { ...this.billingInfo };
    },
    methods: {
        updateData() {
            this.$emit('sendData', this.mutableValues);
        },
        updateInvoiced(invoiced) {
            this.mutableValues = { ...this.mutableValues, invoiced };
            this.updateData();
        },
        getAnalysisGroups() {
            const groupedObj = groupBy(this.billingInfo.analysisRequests.selectedAnalysisGroups, 'id');
            const response = [];
            Object.keys(groupedObj).forEach(key => {
                const aG = groupedObj[key];
                const totalRecurrenceBySample = aG.map(ag => ag.sampleNumber).reduce((a, b) => a + b);
                response.push({
                    count: totalRecurrenceBySample,
                    name: aG[0].name, // first element because all of them are the same
                    ERPCode: aG[0].ERPCode,
                    id: aG[0].id,
                    poNumber: aG[0].poNumber,
                });
            });
            return response;
        },
        getAnalysisSimple() {
            const groupedObj = groupBy(this.billingInfo.analysisRequests.analysisTypes, 'id');
            const response = [];
            Object.keys(groupedObj).forEach(key => {
                const aT = groupedObj[key];
                const totalRecurrenceBySample = aT.map(at => at.sampleNumber).reduce((a, b) => a + b);
                response.push({
                    count: totalRecurrenceBySample,
                    name: aT[0].name, // first element because all of them are the same
                    ERPCode: aT[0].ERPCode,
                    id: aT[0].id,
                    poNumber: aT[0].poNumber,
                });
            });
            return response;
        },
    },
};
</script>
<style lang="scss" scoped>
@import "@/assets/styles/utils/_variables.scss";
.noYPadding{
    padding: 0 6px;
}
.xPadding{
  padding: 0 12rem;
}
@media screen and (max-width: 600px) {
   .xPadding{
      padding: 0;
    }
}
.number {
    border: 1px solid #26384b;
    padding: 5px;
    border-radius: 3px;
}
.analysis-name {
    color: #26384b;
}

.erpCode{
  color: $text-gray;
}
</style>
<style lang="scss">
  .blocSimpleAnalysis col:first-of-type{
    width: 50%;
  }
</style>
